import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFavorites } from "../lib/favorites";
import Seo from "../components/Seo";
import SaveHeart from "../components/SaveHeart";
import IdxDisclaimer from "../components/IdxDisclaimer";
import HoverCyclePhoto from "../components/HoverCyclePhoto";

const fmt = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");

export default function SavedListings() {
  const { user, loading: authLoading, login } = useAuth();
  const { keys, refresh } = useFavorites();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/favorites", { withCredentials: true });
        setItems(data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, keys.size]);

  // Refresh once the auth state resolves in case FavoritesProvider hasn't fetched yet
  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  return (
    <div className="bg-[#F7F4EF] min-h-screen" data-testid="saved-listings-page">
      <Seo
        title="Saved Homes"
        description="Your saved Houston MLS listings — return anytime to compare, tour, or apply for financing."
        path="/saved-listings"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-[#C9A961]" />
          <div className="eyebrow text-[#0A0A0A]">Your Collection</div>
        </div>
        <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] tracking-tight font-light">
          Saved <span className="serif-italic text-[#E63946]">homes.</span>
        </h1>
        <p className="text-[#555] mt-5 max-w-xl font-light">
          Every property you&apos;ve hearted lives here. Come back anytime to compare,
          request a private tour, or start pre-approval on the loan side.
        </p>

        {authLoading || loading ? (
          <div className="mt-16 text-sm text-gray-500">Loading your saved homes…</div>
        ) : !user ? (
          <div className="mt-16 bg-white border border-dashed border-gray-300 rounded-2xl p-14 text-center max-w-2xl">
            <div className="w-14 h-14 rounded-full bg-[#E63946]/10 text-[#E63946] flex items-center justify-center mx-auto mb-5">
              <Heart className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="font-display text-3xl">Sign in to view your saved homes</div>
            <p className="text-[#555] mt-3">
              Create a free account (30 seconds via Google) and every heart you tap will follow you across devices.
            </p>
            <button
              onClick={login}
              className="mt-7 bg-[#E63946] hover:bg-[#c9313d] text-white px-7 py-3.5 rounded-md font-semibold text-sm inline-flex items-center gap-2"
              data-testid="saved-listings-signin"
            >
              Sign in to continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="mt-16 bg-white border border-dashed border-gray-300 rounded-2xl p-14 text-center max-w-2xl">
            <div className="w-14 h-14 rounded-full bg-[#E63946]/10 text-[#E63946] flex items-center justify-center mx-auto mb-5">
              <Heart className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="font-display text-3xl">No saved homes yet</div>
            <p className="text-[#555] mt-3">
              Tap the heart on any listing to save it here. You can also browse the live HAR MLS to get started.
            </p>
            <Link
              to="/mls-listings"
              className="mt-7 inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#333] text-white px-7 py-3.5 rounded-md font-semibold text-sm"
              data-testid="saved-listings-browse"
            >
              Browse MLS listings <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-10 text-sm text-gray-500">
              {items.length} saved {items.length === 1 ? "home" : "homes"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {items.map((l) => (
                <Link
                  key={l.listing_key}
                  to={`/mls-listings/${l.listing_key}`}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group"
                  data-testid={`saved-card-${l.listing_key}`}
                >
                  <div className="relative aspect-[4/3] bg-stone-100">
                    <HoverCyclePhoto photos={l.photos || []} alt={l.address} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded text-[10px] uppercase tracking-[0.15em] font-semibold">
                      {l.status}
                    </div>
                    <SaveHeart
                      listingKey={l.listing_key}
                      address={l.address}
                      className="absolute top-3 right-3"
                    />
                  </div>
                  <div className="p-5">
                    <div className="font-display text-2xl font-semibold">{fmt(l.price)}</div>
                    <div className="text-sm text-[#1A1A1A] mt-1">{l.address}</div>
                    <div className="text-xs text-gray-500">
                      {l.city}, {l.state} {l.zip || ""}
                    </div>
                    <div className="text-xs text-gray-600 mt-2">
                      {l.beds} bd · {l.baths} ba ·{" "}
                      {l.sqft ? Number(l.sqft).toLocaleString() : "—"} sqft
                    </div>
                    <div className="text-[10px] text-gray-400 mt-2 truncate">
                      {l.list_office} · MLS #{l.listing_key}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <IdxDisclaimer />
      </section>
    </div>
  );
}
